import { Alert, Card, Collapse, Flex, Select, Skeleton, Space, Typography } from 'antd';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Button } from '@/atoms/Button';
import {
  AssessmentStepPager,
  type AssessmentFlowStep,
} from '@/components/assessment/AssessmentStepPager';
import { AssetContextModal } from '@/components/common/AssetContextModal';
import { CreateNoteModal } from '@/components/common/CreateNoteModal';
import { AddAssetOptionsForm } from '@/components/layout/AddAssetOptionsForm';
import { BrowseAssetsDrawer } from '@/components/layout/BrowseAssetsDrawer';
import { LinkedDocumentList } from '@/components/layout/LinkedDocumentList';
import { useTranscriptAnalysis } from '@/hooks/useTranscriptAnalysis';

const { Text, Title } = Typography;

/** Props for the `TranscriptAnalysisStep` (Step 2) full UI component. */
export interface TranscriptAnalysisStepProps {
  clientLabel: string;
  projectAssessmentLabel: string;
  assessmentInstanceId: string;
  accessToken: string | null;
  clientId: string;
  projectId: string;
  sharepointReady?: boolean | null;
  microsoftOboAssertion?: string | null;
  currentFlowStep?: AssessmentFlowStep;
  onNavigateAssessmentStep: (step: AssessmentFlowStep) => void;
  onRunAnalysis?: () => void;
  onWorkflowStart?: (
    runId: string,
    streamUrl: string,
    counts: { transcriptCount: number; documentCount: number },
  ) => void;
  onMicrosoftReauth?: () => Promise<void> | void;
  onMicrosoftRefreshObo?: () => Promise<string | null>;
}

/** Step 2 component for uploading transcripts and client documents then triggering the transcript analysis job. */
export const TranscriptAnalysisStep: React.FC<TranscriptAnalysisStepProps> = ({
  clientLabel: _clientLabel,
  projectAssessmentLabel: _projectAssessmentLabel,
  assessmentInstanceId,
  accessToken,
  clientId,
  projectId,
  sharepointReady = null,
  microsoftOboAssertion = null,
  currentFlowStep = 2,
  onNavigateAssessmentStep,
  onRunAnalysis,
  onWorkflowStart,
  onMicrosoftReauth,
  onMicrosoftRefreshObo,
}) => {
  const ta = useTranscriptAnalysis({
    assessmentInstanceId,
    accessToken,
    clientId,
    projectId,
    onRunAnalysis,
    onNavigateAssessmentStep,
    onWorkflowStart,
  });

  if (ta.isLoading) {
    return <Skeleton active paragraph={{ rows: 10 }} />;
  }

  const invalidTranscripts = ta.transcriptResults.filter((r) => !r.valid);
  const invalidClientDocs = ta.clientDocResults.filter((r) => !r.valid);

  return (
    <div className="transcript-analysis-page">
      {/* HEADER — sticky */}
      <div className="transcript-analysis-page__header">
        <Flex wrap="wrap" gap="middle" align="flex-start" justify="space-between">
          <div className="interview-prep-page__hero-heading">
            <div className="interview-prep-page__step-badge" aria-hidden="true">
              2
            </div>
            <div className="interview-prep-page__hero-text">
              <Title level={2} className="interview-prep-page__hero-title">
                Transcript Analysis
              </Title>
              <Text type="secondary" className="interview-prep-page__hero-subtitle">
                Upload interview transcripts and client documents. Blend AI will synthesize
                cross-stakeholder themes and opportunity signals.
              </Text>
            </div>
          </div>
          <Space direction="vertical" align="end">
            <AssessmentStepPager
              current={currentFlowStep}
              onStepChange={onNavigateAssessmentStep}
            />
          </Space>
        </Flex>
      </div>

      {/* BODY — scrollable */}
      <div className="transcript-analysis-page__body">
        {ta.isStep1Approved ? (
          <Alert
            type="success"
            showIcon
            message="Step 01 — Interview Guide Approved"
            description="The interview guide from Step 1 has been reviewed and approved. You can now upload transcripts and start the analysis."
          />
        ) : null}

        {/* Context from Step 1 */}
        <Card
          title="Context from Step 1"
          className="transcript-analysis-page__locked-context"
          extra={
            <Text type="secondary" style={{ fontSize: 'var(--font-size-body-large)' }}>
              Read-only
            </Text>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Stakeholder List
              </Text>
              {ta.stakeholderListMarkdown?.trim() ? (
                <div className="transcript-analysis-page__locked-markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {ta.stakeholderListMarkdown}
                  </ReactMarkdown>
                </div>
              ) : (
                <Text type="secondary">No stakeholder list available.</Text>
              )}
            </div>

            {ta.step1DocCards.length > 0 && (
              <div>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 'var(--font-size-body-large)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  Linked documents from Step 1
                </Text>
                <LinkedDocumentList
                  cardIds={ta.step1DocCards.map((c) => c.cardId)}
                  clientId={clientId}
                  projectId={projectId}
                  accessToken={accessToken ?? ''}
                  onOpen={(cardId) => ta.openContextModal(cardId, null)}
                />
              </div>
            )}

            <Collapse
              ghost
              defaultActiveKey={[]}
              items={[
                {
                  key: 'engagement',
                  label: <Text strong>Engagement Context &amp; Priorities</Text>,
                  children: (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      {ta.engagementContext?.trim() ? (
                        <div className="transcript-analysis-page__locked-markdown">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {ta.engagementContext}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <Text type="secondary">No engagement context available.</Text>
                      )}
                    </Space>
                  ),
                },
              ]}
            />
          </Space>
        </Card>

        {/* Interview Transcripts bucket */}
        <Card
          title={
            <span>
              Interview Transcripts{' '}
              <Text type="danger" style={{ fontSize: 16 }}>
                *
              </Text>
            </span>
          }
          className="transcript-analysis-page__bucket-card"
        >
          <AddAssetOptionsForm
            resetIdentityKey={`${assessmentInstanceId}-transcript-${ta.transcriptResetKey}`}
            onCancel={ta.incrementTranscriptReset}
            onActionComplete={ta.incrementTranscriptReset}
            onCreateNoteNavigate={(id) => ta.openCreateNoteModal(id, 'transcript')}
            onWordDocumentUploaded={(cardId) => {
              ta.incrementTranscriptReset();
              ta.openContextModal(cardId, 'transcript');
            }}
            onBrowseAssets={() => ta.openBrowseDrawer('transcript')}
            sharepointReady={sharepointReady}
            accessToken={accessToken}
            microsoftOboAssertion={microsoftOboAssertion}
            clientId={clientId}
            projectId={projectId}
            onMicrosoftReauth={onMicrosoftReauth}
            onMicrosoftRefreshObo={onMicrosoftRefreshObo}
            intro={
              <Text type="secondary" style={{ fontSize: 13 }}>
                Upload a transcript file (.pdf, .docx), paste transcript text as a note, or browse
                existing assets.
              </Text>
            }
          />

          {ta.transcriptCardIds.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <LinkedDocumentList
                cardIds={ta.transcriptCardIds}
                clientId={clientId}
                projectId={projectId}
                accessToken={accessToken ?? ''}
                onOpen={(cardId) => ta.openContextModal(cardId, 'transcript')}
                onRemove={(cardId) => void ta.unlinkTranscript(cardId)}
              />
              {invalidTranscripts.length > 0 && (
                <div style={{ marginTop: 8 }} data-testid="transcript-preflight-errors">
                  {invalidTranscripts.map((r) => (
                    <Alert
                      key={r.id}
                      type="warning"
                      showIcon
                      style={{ marginBottom: 4 }}
                      message={r.reason ?? 'This document has an issue.'}
                      description={r.recoveryAction ?? undefined}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Client Supporting Documents bucket */}
        <Card
          title="Client Documents (optional but recommended)"
          className="transcript-analysis-page__bucket-card"
        >
          <AddAssetOptionsForm
            resetIdentityKey={`${assessmentInstanceId}-client-doc-${ta.clientDocResetKey}`}
            onCancel={ta.incrementClientDocReset}
            onActionComplete={ta.incrementClientDocReset}
            onCreateNoteNavigate={(id) => ta.openCreateNoteModal(id, 'client-doc')}
            onWordDocumentUploaded={(cardId) => {
              ta.incrementClientDocReset();
              ta.openContextModal(cardId, 'client-doc');
            }}
            onBrowseAssets={() => ta.openBrowseDrawer('client-doc')}
            sharepointReady={sharepointReady}
            accessToken={accessToken}
            microsoftOboAssertion={microsoftOboAssertion}
            clientId={clientId}
            projectId={projectId}
            onMicrosoftReauth={onMicrosoftReauth}
            onMicrosoftRefreshObo={onMicrosoftRefreshObo}
            intro={
              <Text type="secondary" style={{ fontSize: 13 }}>
                Upload reports, presentations, or other client documents to enrich the analysis
                context.
              </Text>
            }
          />

          {ta.clientDocCardIds.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <LinkedDocumentList
                cardIds={ta.clientDocCardIds}
                clientId={clientId}
                projectId={projectId}
                accessToken={accessToken ?? ''}
                onOpen={(cardId) => ta.openContextModal(cardId, 'client-doc')}
                onRemove={(cardId) => void ta.unlinkClientDoc(cardId)}
              />
              {invalidClientDocs.length > 0 && (
                <div style={{ marginTop: 8 }} data-testid="client-doc-preflight-errors">
                  {invalidClientDocs.map((r) => (
                    <Alert
                      key={r.id}
                      type="warning"
                      showIcon
                      style={{ marginBottom: 4 }}
                      message={r.reason ?? 'This document has an issue.'}
                      description={r.recoveryAction ?? undefined}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* FOOTER — sticky */}
      <div className="transcript-analysis-page__footer">
        <Space direction="vertical" align="end" size={4}>
          <Space>
            <Select
              aria-label="Transcript analysis skill"
              style={{ width: 280 }}
              loading={ta.skillsLoading}
              disabled={ta.isLoading}
              value={ta.selectedAnalysisSkillValue ?? undefined}
              onChange={(v) => ta.setSelectedAnalysisSkillValue(String(v))}
              options={ta.analysisSkillOptions}
              placeholder="Select transcript analysis skill"
            />
            <Button
              type="primary"
              disabled={!ta.canStartAnalysis}
              loading={ta.isSaving}
              onClick={() => void ta.startAnalysis()}
            >
              Start Analysis
            </Button>
          </Space>
          {ta.disabledReason && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {ta.disabledReason}
            </Text>
          )}
        </Space>
      </div>

      {/* Browse Assets Drawer */}
      <BrowseAssetsDrawer
        open={ta.browseDrawerBucket !== null}
        onClose={ta.closeBrowseDrawer}
        clientId={clientId}
        projectId={projectId}
        accessToken={accessToken ?? ''}
        linkedCardIds={
          ta.browseDrawerBucket === 'transcript' ? ta.transcriptCardIds : ta.clientDocCardIds
        }
        onSelect={ta.selectFromBrowseDrawer}
      />

      {/* Context Extract Review Modal */}
      <AssetContextModal
        open={ta.contextModalCardId !== null}
        cardId={ta.contextModalCardId}
        accessToken={accessToken}
        clientId={clientId}
        projectId={projectId}
        onLink={(cardId) => void ta.handleContextModalLink(cardId)}
        onClose={ta.closeContextModal}
      />

      {/* Create Note Modal — same layout as AssetContextModal */}
      <CreateNoteModal
        open={ta.createNoteModalId !== null}
        assetSessionId={ta.createNoteModalId}
        accessToken={accessToken}
        clientId={clientId}
        projectId={projectId}
        onClose={ta.closeCreateNoteModal}
        onSaved={ta.handleCreateNoteSaved}
      />
    </div>
  );
};

export default TranscriptAnalysisStep;
